import { useLazyQuery } from "@apollo/client";
import Button from "components/base/Button";
import { Alert } from "components/base/Toast";
import { useModals } from "ModalProvider";
import EditProductView from "pages/manage/products/All/EditProductView";
import ProductLoader from "pages/manage/products/All/ProductLoader";
import { useContext } from "react";
import { FETCH_GTM_SOURCE_PRODUCTS } from "../graphql";
import { GTMProjectContext } from "../GTMProjectContext";
import CreateComponentsView from "../products/CreateComponentsView";
import CreateProductsForSaleView from "../products/CreateProductsForSaleView";
import ProductLinksView from "./ProductLinksView";

const ProductsView = () => {
  const { originalGTMProject, suppliers, refetch } =
    useContext(GTMProjectContext);

  const [fetchSourceProducts, fetchSourceProductsRes] = useLazyQuery(
    FETCH_GTM_SOURCE_PRODUCTS,
    {
      fetchPolicy: "network-only",
    },
  );

  const startUpProjectData = JSON.parse(originalGTMProject.startUpProject.data);

  const modal = useModals();
  const createProductForSaleModal = useModals();
  const createProductModal = useModals();
  const editProductModal = useModals();

  function createComponentsHandler() {
    modal.present({
      title: "Create Components",
      isBeingPresented: true,
      children: (
        <CreateComponentsView
          components={startUpProjectData.components}
          originalGTMProject={originalGTMProject}
          onCreated={modal.hide}
          suppliers={suppliers}
        />
      ),
    });
  }

  function validateCreateProductsForSale(gtmPackagings, startUpPackagingInfos) {
    if (gtmPackagings.length === 0) {
      Alert(
        "error",
        "No packaging found in GTM project. Please create components first.",
      );
      return false;
    }
    if (!startUpPackagingInfos) {
      Alert(
        "error",
        "No packaging info found in StartUp project. Please check your data.",
      );
      return false;
    }
    if (startUpPackagingInfos.length === 0) {
      Alert(
        "error",
        "No packaging options found for Product. Please check your data.",
      );
      return false;
    }

    return true;
  }

  function createProductsForSaleHandler() {
    const gtmPackagings = originalGTMProject.packagings.map((i) => ({
      basic: JSON.parse(i.basic),
      item: JSON.parse(i.item),
    }));
    const startUpPackagingInfos = JSON.parse(
      originalGTMProject.startUpProject.data,
    ).packagings;

    if (!validateCreateProductsForSale(gtmPackagings, startUpPackagingInfos))
      return;

    if (gtmPackagings.length === 1 && startUpPackagingInfos.length === 1) {
      // If there's only one option for both, skip the selection step
      createProductForSaleNextHandler({
        selectedGtmPackaging: gtmPackagings[0],
        selectedStartUpPackaging: startUpPackagingInfos[0],
      });
      return;
    }

    createProductForSaleModal.present({
      title: "Create Products for Sale",
      center: true,
      children: (
        <CreateProductsForSaleView
          originalGTMProject={originalGTMProject}
          nextHandler={createProductForSaleNextHandler}
        />
      ),
    });
  }

  function createProductForSaleNextHandler({
    selectedStartUpPackaging,
    selectedGtmPackaging,
  }) {
    console.log("selectedStartUpPackaging", selectedStartUpPackaging);

    const sourceProductIds = startUpProjectData.components
      .map((c) => c.sourceProductId)
      .filter(Boolean);

    const uniqueSourceProductIds = Array.from(new Set(sourceProductIds));

    const handleNext = (sourceProducts = []) => {
      const complianceData = JSON.parse(
        originalGTMProject.complianceData || "{}",
      );
      const certificates = complianceData.certificates || [];
      const testReports = complianceData.testReports || [];

      const initialValues = {
        name: originalGTMProject.name,
        number: selectedGtmPackaging.item.number,
        defaultQty: selectedStartUpPackaging.orderQty,
        moq: selectedStartUpPackaging.orderQty,
        itemsPerSet: selectedStartUpPackaging.bomQty,
        itemWeight: startUpProjectData.weight,
        company: "AB",
        isActiveForBiz: false,
        activePrices: [],
        children: selectedStartUpPackaging.components.map((component) => {
          let product = originalGTMProject.createdProducts.find(
            (p) => p.gtmProjectComponentId === component.id,
          );
          if (!product && component.sourceProductId) {
            product = sourceProducts.find(
              (p) => p.id === component.sourceProductId,
            );
          }
          return {
            product,
            qty: component.qty,
          };
        }),
        gtmProjectId: originalGTMProject.id,

        considerBomInProduction: true,

        isActive: true,
        isSalable: true,
        isPurchasable: false,

        complianceCertificates: certificates,
        labTestReports: testReports,
      };

      createProductForSaleModal.hide();
      createProductModal.present({
        title: "Create Products",
        fullscreen: true,
        children: (
          <EditProductView
            initialValues={initialValues}
            onSaved={onCreatedHandler}
          />
        ),
      });
    };

    if (uniqueSourceProductIds.length > 0) {
      fetchSourceProducts({
        variables: { ids: uniqueSourceProductIds },
      }).then((res) => {
        const sourceProducts = res.data?.products?.results || [];
        handleNext(sourceProducts);
      });
    } else {
      handleNext([]);
    }
  }

  function onCreatedHandler(product) {
    createProductModal.hide();
    refetch();
    editProductModal.present({
      title: "Edit Product",
      fullscreen: true,
      children: (
        <ProductLoader
          id={product.id}
          hide={() => {
            editProductModal.hide();
            refetch();
          }}
        />
      ),
    });
  }

  const createdComponents = originalGTMProject.createdProducts.filter(
    (i) => i.isPurchasable,
  );
  const productsForSale = originalGTMProject.createdProducts.filter(
    (i) => i.isSalable && i.isActive,
  );

  return (
    <div className="card">
      <h5>
        System Products (<b className="text-orange-600">Stella</b>)
      </h5>
      <div>
        <div className="-mx-2">
          <table>
            {createdComponents.length > 0 && (
              <>
                <thead>
                  <tr>
                    <th>Components</th>
                  </tr>
                </thead>
                <tbody>
                  {originalGTMProject.createdProducts
                    .filter((i) => i.isPurchasable)
                    .map((p) => (
                      <tr
                        key={p.id}
                        className="border-y border-gray-100 dark:border-gray-700"
                      >
                        <td>
                          <ProductLinksView product={p} />
                        </td>
                      </tr>
                    ))}
                </tbody>
              </>
            )}

            {productsForSale.length > 0 && (
              <>
                <thead>
                  <tr>
                    <th>Product for Sale</th>
                  </tr>
                </thead>
                <tbody>
                  {productsForSale.map((p) => (
                    <tr
                      key={p.id}
                      className="border-y border-gray-100 dark:border-gray-700"
                    >
                      <td>
                        <ProductLinksView product={p} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </>
            )}
          </table>
        </div>

        <div className="mt-2 flex space-x-4">
          <Button onClick={createComponentsHandler}>+ Components</Button>
          <Button
            onClick={createProductsForSaleHandler}
            loading={fetchSourceProductsRes.loading}
          >
            + Products for Sale
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ProductsView;
